import { ProjectInfo } from './project-info';

/**
 * Column ids and the saved-view shape, mirroring the server's ProjectGridView.
 *
 * The server resolves the three tiers and normalises the result, so the client
 * renders what it is given rather than re-deriving the cascade. The one rule the
 * server CANNOT apply is availability — it does not know which user-defined
 * fields this caller's projects actually carry without loading the project list —
 * so that single step lives here, in `pruneToAvailableColumns`, and nowhere else.
 */

/** Order and visibility in one list: a column is visible iff it appears. */
export interface ProjectGridView {
  schemaVersion?: number;
  columns: string[];
  /**
   * Every built-in column this view was AWARE OF when it was saved.
   *
   * Without it, "the user hid this" and "this did not exist yet" are the same
   * observation — both are simply an id missing from `columns` — and a new
   * built-in column could never appear for anyone who had ever saved a view.
   * Recording the known set makes the distinction explicit and durable.
   *
   * Optional because views saved before this existed do not carry it; those are
   * read against LEGACY_KNOWN_COLUMNS instead.
   */
  knownColumns?: string[];
  sort: ProjectGridSort[];
  groupBy: string[];
  filters: Record<string, ProjectGridFilter>;
  statusFilter: StatusFilter;
  rowsPerPage: number;
}

export interface ProjectGridSort {
  columnId: string;
  direction: 'asc' | 'desc';
}

export interface ProjectGridFilter {
  op: 'contains' | 'equals' | 'in' | 'between';
  value?: string | null;
  values?: string[] | null;
  from?: string | null;
  to?: string | null;
}

export type StatusFilter = 'all' | 'live' | 'historical';

/**
 * Marks a user-defined field. UDF names are free text typed on the desktop
 * ("Liner Diameter/Thickness", "VeriCure VID#"), so they are namespaced to keep
 * them out of the built-in id space entirely.
 */
export const USER_FIELD_PREFIX = 'up:';

export const isUserFieldColumn = (columnId: string): boolean =>
  columnId.startsWith(USER_FIELD_PREFIX);

export const userFieldName = (columnId: string): string =>
  columnId.slice(USER_FIELD_PREFIX.length);

export const userFieldColumnId = (name: string): string =>
  `${USER_FIELD_PREFIX}${name}`;

/** One selectable value in an enum filter. */
export interface ProjectGridEnumOption {
  /** What the filter matches on: the CELL TEXT, exactly as the grid renders it. */
  value: string;
  /** i18n key for the label. Falls back to `value` when absent. */
  labelKey?: string;
}

export interface ProjectGridColumnDef {
  id: string;
  /** Header text. For a UDF this is the parameter name as the desktop wrote it. */
  label: string;
  /** Numeric and date columns sort and align differently from text. */
  type: 'text' | 'number' | 'date';
  /** Fixed width in px, matching the widths the pane has always used. */
  width: number;
  /**
   * How this column is FILTERED. Text is the default and stays the default.
   *
   * 'enum' is for a column whose vocabulary is FIXED AND KNOWN — a subscription
   * status, an IoT state. Free text there is a worse control in both directions:
   * the operator has to know the exact wording to type, and typing "act" silently
   * matches both "Active" and "Active (Free)" without saying so.
   *
   * DELIBERATELY NOT DATA-DERIVED. Options are declared, not collected from the
   * loaded rows, because a value absent from the current page would vanish from
   * the list — so a filter would offer fewer choices the more you filtered, and
   * "Failed" would disappear from an IoT filter exactly when nothing had failed
   * yet. A derived list is a reasonable thing to want for an open vocabulary like
   * Fiber Type; it is a different feature and is proposed separately.
   */
  filterType?: 'text' | 'enum';
  /** The fixed vocabulary, in display order. Required when filterType is 'enum'. */
  enumOptions?: ProjectGridEnumOption[];
  /**
   * DERIVE the options from the loaded rows instead of declaring them.
   *
   * The opposite trade from a declared vocabulary, and it is only right where the
   * vocabulary is OPEN. Fiber Type is the case Joel approved: its values come from
   * the data, nobody can enumerate them up front, and a text box makes an operator
   * guess at spellings they cannot see.
   *
   * THE COST, which is why this is not the default: a value absent from the loaded
   * rows is absent from the list. Filter to one fiber type and the others vanish
   * from the chooser — so the list must be built from ALL rows, never from the
   * filtered ones. A DECLARED vocabulary does not have that problem, which is why
   * subscription and IoT status stay declared.
   */
  deriveOptions?: boolean;
}

/**
 * The built-in columns, in their default order.
 *
 * Labels are the ones the pane already showed — "Date" rather than "Last
 * Telemetry", "Device Serial No" rather than "Serial" — because renaming a column
 * while moving it into a component would make a refactor look like a redesign to
 * the people using it.
 */
export const BUILT_IN_COLUMNS: readonly ProjectGridColumnDef[] = [
  { id: 'ProjectName',           label: 'Project Name',    type: 'text',   width: 200 },
  { id: 'DeviceName',            label: 'Device Name',     type: 'text',   width: 160 },
  { id: 'LastTelemetryReceived', label: 'Date',            type: 'date',   width: 140 },
  { id: 'SerialNumber',          label: 'Device Serial No',type: 'text',   width: 160 },
  { id: 'CustomerName',          label: 'Customer Name',   type: 'text',   width: 150 },
  {
    id: 'FiberType', label: 'Fiber Type', type: 'text', width: 120,
    // DERIVED, not declared: fiber types come from the data and nobody can
    // enumerate them up front. The options are filled from every loaded project —
    // see deriveEnumOptions, and the note on why it must not read the filtered
    // rows.
    filterType: 'enum', deriveOptions: true,
  },
  { id: 'Version',               label: 'Version',         type: 'text',   width: 120 },
  {
    id: 'Status', label: 'Status', type: 'text', width: 110,
    // Derived from the server's isLive, so the vocabulary is exactly two words
    // and cannot grow. Values are the CELL TEXT the grid renders, because that
    // is what the filter matches on.
    filterType: 'enum',
    enumOptions: [
      { value: 'Live', labelKey: 'GridStatusLive' },
      { value: 'Historical', labelKey: 'GridStatusHistorical' },
    ],
  },
  { id: 'MI',                    label: 'Meas. Interval',  type: 'number', width: 120 },
  { id: 'SamplingInterval',      label: 'Sampling Interval', type: 'number', width: 130 },
  { id: 'CuringTemperature',     label: 'Curing Temp',     type: 'number', width: 120 },
  { id: 'OverheatTemperature',   label: 'Overheat Temp',   type: 'number', width: 130 },
  { id: 'LinerLength',           label: 'Length',          type: 'number', width: 100 },
  { id: 'CmsModel',              label: 'CMS Model',       type: 'text',   width: 140 },
  { id: 'CmsFirmware',           label: 'CMS Firmware',    type: 'text',   width: 130 },
  {
    id: 'Finalized', label: 'Finalized', type: 'text', width: 110,
    filterType: 'enum',
    enumOptions: [
      { value: 'Yes', labelKey: 'GridYes' },
      { value: 'No', labelKey: 'GridNo' },
    ],
  },
];

const BUILT_IN_BY_ID = new Map(BUILT_IN_COLUMNS.map((c) => [c.id, c]));

/**
 * What a saved view that predates `knownColumns` must be assumed to have known.
 *
 * Everything built-in EXCEPT the columns added since. Being explicit matters:
 * treating a legacy view's visible list as its known set would make every
 * column the user ever deliberately hid look "never seen", and merging would
 * silently un-hide all of them. This list keeps the merge to genuinely new
 * columns — today, Device Name.
 *
 * Add an id here at the same time as adding it to BUILT_IN_COLUMNS only if it is
 * NOT new; a genuinely new column must stay out, which is what makes it merge.
 */
export const LEGACY_KNOWN_COLUMNS: readonly string[] = BUILT_IN_COLUMNS.map((c) => c.id).filter(
  (id) => id !== 'DeviceName'
);

export const builtInColumn = (id: string): ProjectGridColumnDef | undefined =>
  BUILT_IN_BY_ID.get(id);

/** Default width for a user-defined field column. */
export const USER_FIELD_COLUMN_WIDTH = 140;

/**
 * The view a customer sees when no tier has saved one.
 *
 * Deliberately the columns the pane already showed, plus Device Name — so a
 * customer who never touches preferences sees what they saw yesterday with one
 * addition, rather than a rearranged grid.
 */
export const DEFAULT_PROJECT_GRID_VIEW: ProjectGridView = {
  schemaVersion: 1,
  columns: [
    // Joel's order. Device Name sits immediately BEFORE the serial so the two
    // identities read together — and, for the many devices with no name, so the
    // muted stand-in sits right beside the serial it is standing in for.
    'ProjectName',
    'DeviceName',
    'SerialNumber',
    'LastTelemetryReceived',
    'FiberType',
    'CustomerName',
    'Version',
  ],
  sort: [{ columnId: 'LastTelemetryReceived', direction: 'desc' }],
  groupBy: [],
  filters: {},
  statusFilter: 'all',
  rowsPerPage: 25,
};

/** A rendered row: either a section heading or a project. */
export interface ProjectGridSectionRow {
  kind: 'section';
  label: string;
}

/**
 * A data row.
 *
 * GENERIC OVER ITS PAYLOAD, so the grid can render something that is not a
 * project. Joel's direction is that this grid becomes the app's standard table,
 * starting with the admin screens — and a table whose row type names one domain
 * model can only ever serve that one screen.
 *
 * The default keeps every existing call site compiling unchanged.
 */
export interface ProjectGridProjectRow<T = ProjectInfo> {
  kind: 'project';
  data: T;
  /**
   * A stable identity for trackBy, PRECOMPUTED like everything else here.
   *
   * The grid used to derive this from row.data.ProjectName, which is the one
   * remaining thing that would have kept it tied to projects. A row knows its own
   * identity better than the grid does, and computing it once here costs less
   * than a template call per row per cycle — the mistake this file exists to
   * document.
   */
  key: string;
  /**
   * Cell text, ALIGNED TO `columns` AND PRECOMPUTED.
   *
   * This is the whole point of the view model. The pane used to resolve every
   * cell through a component getter during change detection, and
   * getAllUserParameterNames() — a full scan of every project's every parameter —
   * ran once per cell, inside an *ngFor nested in an *ngFor. Six UDF columns and
   * a hundred rows made that six hundred full scans per cycle, on an iPad.
   */
  cells: string[];

  /**
   * Per-cell: is this text a STAND-IN rather than a real value?
   *
   * Today only Device Name has one — an unnamed device shows its serial. With
   * that column default-visible directly beside Device Serial No, and exactly
   * one named device in the fleet right now, most rows would otherwise render
   * the same string twice in identical styling and read as a duplicated column.
   * The muted treatment is what says "unnamed", so it carries real meaning.
   *
   * PRECOMPUTED, aligned to `cells`, for the same reason the cell text is: this
   * file already paid for resolving per-cell values during change detection, and
   * a template method here would reintroduce exactly that cost per cell per
   * cycle.
   */
  standIn: boolean[];
}

export type ProjectGridRow<T = ProjectInfo> = ProjectGridSectionRow | ProjectGridProjectRow<T>;

export interface ProjectGridModel<T = ProjectInfo> {
  columns: ProjectGridColumnDef[];
  rows: ProjectGridRow<T>[];
  /**
   * Row tallies for the summary line. `live` and `historical` are project
   * concepts; a non-project consumer leaves them at zero and shows only `total`
   * and `filtered`, rather than the grid inventing a meaning for them.
   */
  counts: { total: number; live: number; historical: number; filtered: number };
}
